import { createEnv } from "@t3-oss/env-nextjs";
import { z } from "zod";

// Build-time validation. Missing or malformed vars fail the build, not the
// first request. Server-only secrets MUST NOT be prefixed with `NEXT_PUBLIC_`.
export const env = createEnv({
  server: {
    KUDU_API_INTERNAL_URL: z.string().url(),
    /**
     * HMAC secret for the `X-Kudu-Edge-Auth` envelope kudu-web mints
     * when calling kudu-api's edge-only GraphQL fields
     * (`tenantByHost` + the public-intake mutations). Must match
     * `EDGE_HMAC_SECRET` on the kudu-api side. Optional in
     * development; required in production via the runtime check in
     * `middleware.ts`. See MULTITENANT_PLAN.md § Phase 1.
     */
    KUDU_EDGE_HMAC_SECRET: z.string().min(32).optional(),
    /**
     * Comma-separated marketing/apex hosts for the pay-first self-serve
     * signup `(platform)` route group (SELF_SERVE_SIGNUP_PLAN §3, §7.1) —
     * e.g. `oluflow.com,www.oluflow.com`. Server-only (no
     * `NEXT_PUBLIC_`). `middleware.ts` reads `process.env` directly (Edge
     * runtime); this declaration is for the Node-runtime side + build-time
     * validation. Optional — empty disables the platform surface.
     */
    KUDU_PLATFORM_HOSTS: z.string().optional(),
    SENTRY_DSN: z.string().optional(),
    S3_UPLOADS_BUCKET: z.string().optional(),
  },
  client: {
    // Apollo URI. Accepts an absolute URL (single-origin dev) or a
    // path like `/graphql` (multi-tenant production, where the URL
    // is same-origin per the current page host so the `__Host-`
    // session cookie flows). z.string().url() would reject the path.
    NEXT_PUBLIC_API_URL: z.string().min(1),
    NEXT_PUBLIC_APP_ENV: z.enum(["development", "staging", "production"]),
    NEXT_PUBLIC_POSTHOG_HOST: z.string().url().optional(),
    NEXT_PUBLIC_POSTHOG_KEY: z.string().optional(),
    NEXT_PUBLIC_SITE_URL: z.string().url().optional(),
    /**
     * Stripe publishable key used by the marketing /checkout page to
     * mount Stripe Elements (Payment Element). The platform secret
     * key lives on the server (Phase 9 § S1); this publishable
     * counterpart is safe to ship to the browser. Optional in
     * development so the page renders with a "payments unavailable"
     * notice rather than failing the build; required at runtime in
     * production.
     */
    NEXT_PUBLIC_STRIPE_PUBLISHABLE_KEY: z.string().optional(),
    /**
     * Customer-facing platform brand name shown in the `(platform)` self-serve
     * signup marketing/UI copy (SELF_SERVE_SIGNUP_PLAN "build now, brand
     * later"). Set at brand cutover; optional so the surface builds with a
     * placeholder beforehand. The internal codename "Kudu" is NEVER
     * customer-facing — never hardcode it here.
     */
    NEXT_PUBLIC_BRAND_NAME: z.string().optional(),
    /**
     * Parent domain shown as the `.oluflow.com` suffix on the signup form's
     * subdomain field (display only). Mirrors the server-side
     * `KUDU_TENANT_SUBDOMAIN_PARENT`; set both at brand cutover.
     */
    NEXT_PUBLIC_TENANT_SUBDOMAIN_PARENT: z.string().optional(),
  },
  runtimeEnv: {
    KUDU_API_INTERNAL_URL: process.env.KUDU_API_INTERNAL_URL,
    KUDU_EDGE_HMAC_SECRET: process.env.KUDU_EDGE_HMAC_SECRET,
    KUDU_PLATFORM_HOSTS: process.env.KUDU_PLATFORM_HOSTS,
    SENTRY_DSN: process.env.SENTRY_DSN,
    S3_UPLOADS_BUCKET: process.env.S3_UPLOADS_BUCKET,
    NEXT_PUBLIC_API_URL: process.env.NEXT_PUBLIC_API_URL,
    NEXT_PUBLIC_APP_ENV: process.env.NEXT_PUBLIC_APP_ENV,
    NEXT_PUBLIC_POSTHOG_HOST: process.env.NEXT_PUBLIC_POSTHOG_HOST,
    NEXT_PUBLIC_POSTHOG_KEY: process.env.NEXT_PUBLIC_POSTHOG_KEY,
    NEXT_PUBLIC_SITE_URL: process.env.NEXT_PUBLIC_SITE_URL,
    NEXT_PUBLIC_STRIPE_PUBLISHABLE_KEY: process.env.NEXT_PUBLIC_STRIPE_PUBLISHABLE_KEY,
    NEXT_PUBLIC_BRAND_NAME: process.env.NEXT_PUBLIC_BRAND_NAME,
    NEXT_PUBLIC_TENANT_SUBDOMAIN_PARENT: process.env.NEXT_PUBLIC_TENANT_SUBDOMAIN_PARENT,
  },
  emptyStringAsUndefined: true,
  // Skip validation in CI builds that intentionally have no env wired up
  // (e.g. `next build` during the bundle-size gate). Set in those scripts.
  skipValidation: process.env.SKIP_ENV_VALIDATION === "1",
});
